import React from "react";
import {
  Paper,
  Divider,
  Stack,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box
} from "@mui/material";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BorderAllIcon from "@mui/icons-material/BorderAll";
import BorderClearIcon from "@mui/icons-material/BorderClear";

import { useTheme } from "@mui/material/styles";

const themeColorOptions = [
  { label: "Text Primary", value: "text.primary" },
  { label: "Text Secondary", value: "text.secondary" },
  { label: "Primary Main", value: "primary.main" },
  { label: "Secondary Main", value: "secondary.main" },
  { label: "Error Main", value: "error.main" },
  { label: "Warning Main", value: "warning.main" },
  { label: "Info Main", value: "info.main" },
  { label: "Success Main", value: "success.main" },
  { label: "Black", value: "black" },
  { label: "White", value: "white" }
];

const dateFieldTypes = [
  "DATE",
  "DATETIME",
  "CREATEDATETIME",
  "UPDATEDATETIME"
];

const dateFormatOptions = [
  "yyyy-MM-dd",
  "MM/dd/yyyy",
  "dd/MM/yyyy",
  "MMM dd, yyyy",
  "dd MMM yyyy",
  "yyyy-MM-dd HH:mm:ss",
  "MMM dd, yyyy hh:mm a",
  "dd MMM yyyy hh:mm a"
];



export default function PropertiesPanel({
  element,
  fieldGroups = {},
  onChange,
  onDelete
}) {
  const theme = useTheme();

  const getColorPreview = value => {
    if (value === "transparent") return "transparent";
    if (value === "black" || value === "white") return value;

    const [section, key] = value.split(".");
    return theme?.palette?.[section]?.[key] || value;
  };

  if (!element) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Properties</Typography>
        <Typography variant="body2">Select an item.</Typography>
      </Paper>
    );
  }

  const update = patch => {
    onChange({ ...element, ...patch });
  };

  const updateStyle = patch => {
    const updated = {
      ...element,
      style: {
        ...(element.style || {}),
        ...patch
      }
    };

    onChange(updated);
  };

  

  const updateColumn = (index, patch) => {
    const columns = [...(element.columns || [])];
    columns[index] = {
      ...columns[index],
      ...patch
    };
    update({ columns });
  };

  const moveColumn = (index, direction) => {
    const columns = [...(element.columns || [])];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= columns.length) return;

    const temp = columns[index];
    columns[index] = columns[targetIndex];
    columns[targetIndex] = temp;

    update({ columns });
  };

  const detailFields =
    fieldGroups["Transaction Details Fields"] ||
    fieldGroups["Transaction Details"] ||
    [];

  const isTextElement = ["label", "typography", "field"].includes(element.type);
  
  const renderColorMenuItems = includeTransparent => {
  const items = [];

  if (includeTransparent) {
    items.push(
      <MenuItem key="transparent" value="transparent">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "transparent"
            }}
          />
          Transparent
        </Box>
      </MenuItem>
    );
  }

  themeColorOptions.forEach(color => {
    items.push(
      <MenuItem key={color.value} value={color.value}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: getColorPreview(color.value)
            }}
          />
          {color.label}
        </Box>
      </MenuItem>
    );
  });

  return items;
};

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6">Properties</Typography>
      <Divider sx={{ my: 1 }} />

      <Stack spacing={2}>
        <TextField
          label="Type"
          value={
            element.type === "field"
              ? element.label || "Field"
              : element.label || element.type
          }
          size="small"
          disabled
        />

        {(element.type === "label" || element.type === "typography") && (
          <TextField
            label="Text"
            value={element.text || ""}
            size="small"
            onChange={e => update({ text: e.target.value })}
          />
        )}

        <TextField
          label="X"
          type="number"
          value={element.x || 0}
          size="small"
          onChange={e => update({ x: Number(e.target.value) })}
        />

        <TextField
          label="Y"
          type="number"
          value={element.y || 0}
          size="small"
          onChange={e => update({ y: Number(e.target.value) })}
        />

        <TextField
          label="Width"
          type="number"
          value={element.width || 0}
          size="small"
          onChange={e => update({ width: Number(e.target.value) })}
        />

        <TextField
          label="Height"
          type="number"
          value={element.height || 0}
          size="small"
          onChange={e => update({ height: Number(e.target.value) })}
        />

        {isTextElement && (
          <>
            <TextField
              label="Font Size"
              type="number"
              value={element.style?.fontSize || 12}
              size="small"
              onChange={e => updateStyle({ fontSize: Number(e.target.value) })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(element.style?.bold)}
                  onChange={e => updateStyle({ bold: e.target.checked })}
                />
              }
              label="Bold"
            />

            <Typography variant="subtitle2">Text Alignment</Typography>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={element.style?.textAlign || "left"}
              onChange={(e, value) => value && updateStyle({ textAlign: value })}
            >
              <ToggleButton value="left">
                <FormatAlignLeftIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center">
                <FormatAlignCenterIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="right">
                <FormatAlignRightIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="subtitle2">Vertical Alignment</Typography>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={element.style?.verticalAlign || "top"}
              onChange={(e, value) =>
                value && updateStyle({ verticalAlign: value })
              }
            >
              <ToggleButton value="top">
                <VerticalAlignTopIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="middle">
                <VerticalAlignCenterIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="bottom">
                <VerticalAlignBottomIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              select
              size="small"
              label="Text Color"
              value={element.style?.color || "text.primary"}
              onChange={e => updateStyle({ color: e.target.value })}
              fullWidth
            >
              {renderColorMenuItems(false)}
            </TextField>

            <TextField
              select
              size="small"
              label="Background Color"
              value={element.style?.backgroundColor || "transparent"}
              onChange={e =>
                updateStyle({ backgroundColor: e.target.value })
              }
              fullWidth
            >
              {renderColorMenuItems(true)}
            </TextField>
          </>
        )}

        {element.type === "field" &&
          dateFieldTypes.includes(String(element.fieldType || element.dataType || "").toUpperCase()) && (
            <TextField
              select
              label="Date Format"
              size="small"
              value={element.format || "yyyy-MM-dd"}
              onChange={e => update({ format: e.target.value })}
              fullWidth
            >
              {dateFormatOptions.map(format => (
                <MenuItem key={format} value={format}>
                  {format}
                </MenuItem>
              ))}
            </TextField>
          )}

        {element.type === "transactionDetailTable" && (
          <>
            <Typography variant="subtitle2">Table Settings</Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showHeader !== false}
                  onChange={e => update({ showHeader: e.target.checked })}
                />
              }
              label="Show Header"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.repeatHeaderOnPageBreak !== false}
                  onChange={e =>
                    update({ repeatHeaderOnPageBreak: e.target.checked })
                  }
                />
              }
              label="Repeat Header on Page Break"
            />

            <TextField
              label="Header Height"
              type="number"
              size="small"
              value={element.headerHeight || 26}
              onChange={e => update({ headerHeight: Number(e.target.value) })}
            />

            <TextField
              label="Row Height"
              type="number"
              size="small"
              value={element.rowHeight || 24}
              onChange={e => update({ rowHeight: Number(e.target.value) })}
            />

            <Divider />

            <Typography variant="subtitle2">Add Column</Typography>

            <TextField
              select
              size="small"
              label="Detail Field"
              value=""
              onChange={e => {
                const fieldPath = e.target.value;
                const selected = detailFields.find(
                  field => field.fieldPath === fieldPath
                );

                if (!selected) return;

                const exists = (element.columns || []).some(
                  col => col.fieldPath === selected.fieldPath
                );

                if (exists) return;

                update({
                  columns: [
                    ...(element.columns || []),
                    {
                      label: selected.label,
                      fieldPath: selected.fieldPath,
                      fieldType: selected.fieldType,
                      dataType: selected.dataType,
                      format: selected.format || "",
                      width: 120,
                      headerAlign: "center",
                      detailAlign: "left",
                      headerVerticalAlign: "middle",
                      detailVerticalAlign: "middle",
                      headerBackgroundColor: "transparent",
                      headerColor: "text.primary",
                      headerFontSize: 12,
                      headerBold: false,
                      showHeaderCellLines: true,
                      showDetailCellLines: true
                    }
                  ]
                });
              }}
              fullWidth
            >
              {detailFields.map(field => (
                <MenuItem key={field.fieldPath} value={field.fieldPath}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <Box
  sx={{
    maxHeight: "45vh",
    overflowY: "auto",
    pr: 1
  }}
>
  {(element.columns || []).map((col, index) => (
    <Accordion key={col.fieldPath} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 1
          }}
        >
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ flexGrow: 1 }}
          >
            {col.label || `Column ${index + 1}`}
          </Typography>

          <IconButton
            size="small"
            disabled={index === 0}
            onClick={e => {
              e.stopPropagation();
              moveColumn(index, -1);
            }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            disabled={index === (element.columns || []).length - 1}
            onClick={e => {
              e.stopPropagation();
              moveColumn(index, 1);
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={e => {
              e.stopPropagation();
              update({
                columns: (element.columns || []).filter(
                  (_, i) => i !== index
                )
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={1}>
          <TextField
            label="Header Caption"
            size="small"
            value={col.label}
            onChange={e =>
              updateColumn(index, { label: e.target.value })
            }
          />

          <TextField
            label="Width"
            type="number"
            size="small"
            value={col.width || 120}
            onChange={e =>
              updateColumn(index, { width: Number(e.target.value) })
            }
          />

          <TextField
            select
            size="small"
            label="Header Background"
            value={col.headerBackgroundColor || "transparent"}
            onChange={e =>
              updateColumn(index, {
                headerBackgroundColor: e.target.value
              })
            }
          >
            {renderColorMenuItems(true)}
          </TextField>

          <TextField
            select
            size="small"
            label="Header Font Color"
            value={col.headerColor || "text.primary"}
            onChange={e =>
              updateColumn(index, { headerColor: e.target.value })
            }
          >
            {renderColorMenuItems(false)}
          </TextField>

          <TextField
            label="Header Font Size"
            type="number"
            size="small"
            value={col.headerFontSize || 12}
            onChange={e =>
              updateColumn(index, { headerFontSize: Number(e.target.value) })
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(col.headerBold)}
                onChange={e =>
                  updateColumn(index, { headerBold: e.target.checked })
                }
              />
            }
            label="Header Bold"
          />

          <TextField
            select
            label="Header Alignment"
            size="small"
            value={col.headerAlign || "center"}
            onChange={e =>
              updateColumn(index, { headerAlign: e.target.value })
            }
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </TextField>

          <TextField
            select
            label="Detail Alignment"
            size="small"
            value={col.detailAlign || "left"}
            onChange={e =>
              updateColumn(index, { detailAlign: e.target.value })
            }
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </TextField>

          <TextField
              label="Detail Font Size"
              type="number"
              size="small"
              value={col.detailFontSize || 12}
              onChange={e =>
                  updateColumn(index, {
                      detailFontSize: Number(e.target.value)
                  })
              }
          />

          <FormControlLabel
              control={
                  <Checkbox
                      checked={Boolean(col.detailBold)}
                      onChange={e =>
                          updateColumn(index, {
                              detailBold: e.target.checked
                          })
                      }
                  />
              }
              label="Detail Bold"
          />

          {dateFieldTypes.includes(
              String(col.fieldType || col.dataType || "").toUpperCase()
            ) && (
              <TextField
                select
                label="Detail Date Format"
                size="small"
                value={col.format || "yyyy-MM-dd"}
                onChange={e =>
                  updateColumn(index, { format: e.target.value })
                }
                fullWidth
              >
                {dateFormatOptions.map(format => (
                  <MenuItem key={format} value={format}>
                    {format}
                  </MenuItem>
                ))}
              </TextField>
            )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption">Header Lines</Typography>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={col.showHeaderCellLines !== false ? "show" : "hide"}
              onChange={(e, value) => {
                if (!value) return;
                updateColumn(index, {
                  showHeaderCellLines: value === "show"
                });
              }}
            >
              <ToggleButton value="show">
                <BorderAllIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="hide">
                <BorderClearIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption">Row Lines</Typography>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={col.showDetailCellLines !== false ? "show" : "hide"}
              onChange={(e, value) => {
                if (!value) return;
                updateColumn(index, {
                  showDetailCellLines: value === "show"
                });
              }}
            >
              <ToggleButton value="show">
                <BorderAllIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="hide">
                <BorderClearIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  ))}
</Box>
          </>
        )}

        {element.type === "summaryBand" && (
          <>
            <Typography variant="subtitle2">Summary Band</Typography>

            <TextField
              label="Height"
              type="number"
              size="small"
              value={element.height || 120}
              onChange={e =>
                update({
                  height: Number(e.target.value)
                })
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showOnEmpty !== false}
                  onChange={e =>
                    update({
                      showOnEmpty: e.target.checked
                    })
                  }
                />
              }
              label="Show when no detail rows"
            />
          </>
        )}

        {element.type === "groupHeaderBand" && (
          <>
            <Divider />

            <Typography variant="subtitle2">
              Group Settings
            </Typography>

            <TextField
              label="Group Detail Field Path"
              size="small"
              value={element.groupFieldPath || ""}
              onChange={e =>
                update({
                  groupFieldPath: e.target.value
                })
              }
              helperText="Example: tabledetails.sTaxCode"
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.repeatOnPageBreak !== false}
                  onChange={e =>
                    update({
                      repeatOnPageBreak:
                        e.target.checked
                    })
                  }
                />
              }
              label="Repeat group header on page break"
            />
          </>
        )}

        {element.type === "summaryTaxList" && (
          <>
            <Divider />

            <Typography variant="subtitle2">
              Tax List
            </Typography>

            <TextField
              select
              size="small"
              label="Display Value"
              value={
                element.taxValueMode ||
                "CALCULATED_TAX"
              }
              onChange={e =>
                update({
                  taxValueMode: e.target.value
                })
              }
              fullWidth
            >
              <MenuItem value="CALCULATED_TAX">
                Calculated Tax
              </MenuItem>

              <MenuItem value="TAXABLE_AMOUNT">
                Taxable Amount
              </MenuItem>
            </TextField>

            <TextField
              label="Row Height"
              type="number"
              size="small"
              value={element.rowHeight ?? 18}
              onChange={e =>
                update({
                  rowHeight: Number(e.target.value)
                })
              }
              fullWidth
            />

            <TextField
              label="Value Width"
              type="number"
              size="small"
              value={element.valueWidth ?? 80}
              onChange={e =>
                update({
                  valueWidth: Number(e.target.value)
                })
              }
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showTaxCode === true}
                  onChange={e =>
                    update({
                      showTaxCode: e.target.checked
                    })
                  }
                />
              }
              label="Show tax code"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showTaxName !== false}
                  onChange={e =>
                    update({
                      showTaxName: e.target.checked
                    })
                  }
                />
              }
              label="Show tax name"
            />
          </>
        )}
        
        {element.type === "summaryWithholdingList" && (
          <>
            <Divider />

            <Typography variant="subtitle2">
              Withholding Tax List
            </Typography>

            <TextField
              label="Row Height"
              type="number"
              size="small"
              value={element.rowHeight ?? 18}
              onChange={e =>
                update({
                  rowHeight: Number(e.target.value)
                })
              }
              fullWidth
            />

            <TextField
              label="Value Width"
              type="number"
              size="small"
              value={element.valueWidth ?? 80}
              onChange={e =>
                update({
                  valueWidth: Number(e.target.value)
                })
              }
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showWithholdingCode === true}
                  onChange={e =>
                    update({
                      showWithholdingCode: e.target.checked
                    })
                  }
                />
              }
              label="Show withholding code"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={element.showWithholdingName !== false}
                  onChange={e =>
                    update({
                      showWithholdingName: e.target.checked
                    })
                  }
                />
              }
              label="Show withholding description"
            />
          </>
        )}
        <Button
          color="error"
          variant="outlined"
          onClick={() => onDelete(element.id)}
        >
          Delete
        </Button>
      </Stack>
    </Paper>
  );
}